import { Controller } from "@hotwired/stimulus"
import { track } from "@amplitude/unified"
import { deriveStep2UIState } from "./idp_modal_step2_state"

const ORG_ID_PATTERN = /^itdr-mapping-org-(\d+)$/

export default class extends Controller {
  static targets = [
    "step1", "step2", "step3",
    "select", "error",
    "ispmCheckbox", "ispmTooltip", "ispmSubtitle",
    "itdrCheckbox", "itdrTooltip", "itdrSubtitle",
    "microsoftCheckbox", "microsoftTooltip", "microsoftProviderSubtitle",
    "microsoftSection", "microsoftSignInButton", "microsoftConnectedBadge",
    "microsoftSubtitle", "microsoftEnableProductButton", "microsoftEnableProductLabel",
    "microsoftOauthForm", "microsoftOauthOrgId", "microsoftOauthOrgName",
    "microsoftOauthProductItdr", "microsoftOauthProductIspm",
    "microsoftErrorSection", "microsoftErrorMessage",
    "googleCheckbox", "googleTooltip",
    "googleSection", "googleStartCard", "googleHeaderSpinner", "googleExpandableBody",
    "googleExpandableDivider", "googleStep1Card", "googleStep2Card", "googleConnectedBadge",
    "googleSignInButton", "googleOauthForm", "googleOauthOrgId", "googleOauthOrgName",
    "googleErrorSection", "googleErrorMessage",
    "gccHighCheckbox", "gccHighWarning", "gccHighSection", "gccHighStepper", "standardStepper", "standardHeader",
    "gccHighForm", "gccHighOrgId", "gccHighOrgName", "gccHighScriptOutput", "gccHighOrgConflictError",
    "cancelButton", "continueButton"
  ]

  static values = {
    microsoftMappedOrgIds: Array,
    googleMappedOrgIds: Array,
    itdrEntitledOrgIds: Array,
    ispmEntitledOrgIds: Array,
    canAccessItdr: Boolean,
    canAccessIspm: Boolean,
    ispmVisible: Boolean,
  }

  static outlets = ["ui--combobox"]

  #currentStep = 1
  #orgId = null
  #orgName = null
  #microsoftMapped = false
  #googleMapped = false
  #itdrEntitled = false
  #ispmEntitled = false
  #microsoftSelected = false
  #googleSelected = false
  #microsoftConnected = false
  #googleConnected = false
  #microsoftError = null
  #googleError = null
  #gccHighSelected = false
  #popups = {}
  #pollTimers = {}

  selectionChanged() {
    this.errorTarget.hidden = true
    if (this.hasGccHighOrgConflictErrorTarget) this.gccHighOrgConflictErrorTarget.hidden = true
    this.continueButtonTarget.disabled = !this.selectTarget.value
  }

  advance() {
    if (this.#currentStep === 1) {
      this.#advanceFromStep1()
    } else if (this.#currentStep === 2) {
      track("Identity Provider Modal - Step Completed", { step: 2 })
      this.#goToStep(3)
    } else if (this.#currentStep === 3) {
      this.#finish()
    }
  }

  gccHighChanged() {
    this.#gccHighSelected = this.hasGccHighCheckboxTarget && this.gccHighCheckboxTarget.checked
    if (this.hasGccHighWarningTarget) this.gccHighWarningTarget.hidden = !this.#gccHighSelected
    if (this.hasGccHighOrgConflictErrorTarget) this.gccHighOrgConflictErrorTarget.hidden = true
    if (this.#gccHighSelected) track("Identity Provider Modal - GCC High Selected")
  }

  gccHighScriptOutputChanged() {
    this.#updateGccHighContinueButton()
  }

  connectMicrosoft() {
    const popupTarget = this.microsoftOauthFormTarget.target
    this.#popups.microsoft = window.open("", popupTarget, "toolbar=no,location=no,menubar=no,width=600,height=700")

    if (!this.#popups.microsoft || this.#popups.microsoft.closed) {
      this.#showProviderError("microsoft", "Popup was blocked. Please allow popups for this site and try again.")
      return
    }

    track("Identity Provider Modal - Microsoft Configuration Started")
    this.#microsoftError = null
    this.#hideProviderError("microsoft")
    this.#updateStep3ContinueButton()
    this.microsoftSignInButtonTarget.disabled = true
    this.microsoftEnableProductButtonTarget.disabled = true

    if (this.#orgId) {
      this.microsoftOauthOrgIdTarget.value = this.#orgId
      this.microsoftOauthOrgNameTarget.disabled = true
    } else if (this.#orgName) {
      this.microsoftOauthOrgNameTarget.value = this.#orgName
      this.microsoftOauthOrgIdTarget.disabled = true
    }

    // Thread selected products into the OAuth form
    this.microsoftOauthProductItdrTarget.disabled = !this.itdrCheckboxTarget.checked
    this.microsoftOauthProductIspmTarget.disabled = !this.ispmCheckboxTarget.checked

    this.microsoftOauthFormTarget.submit()

    this.#startPollTimer("microsoft")
  }

  oauthSuccess(provider) {
    this.#clearPollTimer(provider)
    if (provider === "microsoft") {
      this.#microsoftConnected = true
      this.#microsoftError = null
      this.microsoftSignInButtonTarget.hidden = true
      this.microsoftEnableProductButtonTarget.hidden = true
      this.microsoftConnectedBadgeTarget.hidden = false
      track("Identity Provider Modal - Microsoft Connected")
    } else if (provider === "google") {
      this.#googleConnected = true
      this.#googleError = null
      this.googleSignInButtonTarget.hidden = true
      this.googleConnectedBadgeTarget.hidden = false
      track("Identity Provider Modal - Google Connected")
    }
    this.#updateStep3ContinueButton()
    this.#hideProviderError(provider)
  }

  oauthError(provider, message) {
    this.#clearPollTimer(provider)
    if (provider === "microsoft") {
      this.#microsoftError = message || "Authorization failed. Please try again."
      this.microsoftSignInButtonTarget.disabled = false
    } else if (provider === "google") {
      this.#googleError = message || "Authorization failed. Please try again."
      this.googleSignInButtonTarget.disabled = false
    }
    this.#showProviderError(provider, message)
    this.#updateStep3ContinueButton()
  }

  connectGoogle() {
    const popupTarget = this.googleOauthFormTarget.target
    this.#popups.google = window.open("", popupTarget, "toolbar=no,location=no,menubar=no,width=600,height=700")

    if (!this.#popups.google || this.#popups.google.closed) {
      this.#showProviderError("google", "Popup was blocked. Please allow popups for this site and try again.")
      return
    }

    this.#googleError = null
    this.#hideProviderError("google")
    this.#updateStep3ContinueButton()
    this.googleSignInButtonTarget.disabled = true

    if (this.#orgId) {
      this.googleOauthOrgIdTarget.value = this.#orgId
      this.googleOauthOrgNameTarget.disabled = true
    } else if (this.#orgName) {
      this.googleOauthOrgNameTarget.value = this.#orgName
      this.googleOauthOrgIdTarget.disabled = true
    }
    this.googleOauthFormTarget.submit()

    this.#startPollTimer("google")
  }

  startGoogleConfiguration() {
    track("Identity Provider Modal - Google Configuration Started")
    this.googleStartCardTarget.hidden = true
    this.googleHeaderSpinnerTarget.hidden = false
    this.googleStep1CardTarget.hidden = false
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.googleExpandableBodyTarget.style.gridTemplateRows = "1fr"
      })
    })
  }

  advanceGoogleToStep2() {
    this.googleStep1CardTarget.hidden = true
    this.googleStep2CardTarget.hidden = false
  }

  advanceGoogleToStep3() {
    this.googleExpandableBodyTarget.style.gridTemplateRows = "0fr"
    this.googleHeaderSpinnerTarget.hidden = true
    this.googleSignInButtonTarget.hidden = false
  }

  disconnect() {
    for (const provider of ["microsoft", "google"]) {
      this.#clearPollTimer(provider)
      if (this.#popups[provider] && !this.#popups[provider].closed) this.#popups[provider].close()
    }
  }

  reset(event) {
    if (event?.type === "ui--modal:closed" && !event.target.contains(this.element)) return

    this.#orgId = null
    this.#orgName = null
    this.#microsoftMapped = false
    this.#googleMapped = false
    this.#itdrEntitled = false
    this.#ispmEntitled = false

    this.errorTarget.hidden = true
    if (this.hasGccHighOrgConflictErrorTarget) this.gccHighOrgConflictErrorTarget.hidden = true

    if (this.hasUiComboboxOutlet) this.uiComboboxOutlet.choices?.removeActiveItems()

    this.selectTarget.closest(".choices")?.classList.remove("is-error")
    this.cancelButtonTarget.hidden = false
    this.continueButtonTarget.disabled = true
    this.#gccHighSelected = false
    if (this.hasGccHighCheckboxTarget) this.gccHighCheckboxTarget.checked = false
    if (this.hasGccHighWarningTarget) this.gccHighWarningTarget.hidden = true
    this.#resetStep2()
    this.#resetStep3()
    this.#goToStep(1)
    track("Identity Provider Modal - Opened")
  }

  productChanged() {
    this.#applyStep2UIState(this.#computeStep2UIState())
  }

  providerChanged() {
    const prevMicrosoft = this.#microsoftSelected
    const prevGoogle = this.#googleSelected
    this.#microsoftSelected = this.microsoftCheckboxTarget.checked
    this.#googleSelected = this.googleCheckboxTarget.checked
    if (!prevMicrosoft && this.#microsoftSelected) track("Identity Provider Modal - Provider Selected", { provider: "microsoft" })
    if (!prevGoogle && this.#googleSelected) track("Identity Provider Modal - Provider Selected", { provider: "google" })
    this.#applyStep2UIState(this.#computeStep2UIState())
  }

  #advanceFromStep1() {
    const value = this.selectTarget.value

    if (!value) {
      this.errorTarget.hidden = false
      return
    }

    const match = value.match(ORG_ID_PATTERN)
    this.#orgId = match ? match[1] : null
    this.#orgName = match ? null : value
    this.#microsoftMapped = !!this.#orgId && this.microsoftMappedOrgIdsValue.includes(this.#orgId)
    this.#googleMapped = !!this.#orgId && this.googleMappedOrgIdsValue.includes(this.#orgId)
    this.#itdrEntitled = !!this.#orgId && this.itdrEntitledOrgIdsValue.includes(this.#orgId)
    this.#ispmEntitled = !!this.#orgId && this.ispmEntitledOrgIdsValue.includes(this.#orgId)

    if (this.#gccHighSelected && this.#microsoftMapped) {
      if (this.hasGccHighOrgConflictErrorTarget) this.gccHighOrgConflictErrorTarget.hidden = false
      return
    }

    track("Identity Provider Modal - Step Completed", { step: 1 })

    if (this.#gccHighSelected) {
      this.#goToStep(3)
      return
    }

    this.#resetStep2()
    this.#goToStep(2)
    this.#applyStep2UIState(this.#computeStep2UIState())
  }

  #goToStep(step) {
    this.#currentStep = step
    this.step1Target.hidden = step !== 1
    this.step2Target.hidden = step !== 2
    this.step3Target.hidden = step !== 3

    if (step === 3 && this.#gccHighSelected) {
      if (this.hasStandardStepperTarget) this.standardStepperTarget.hidden = true
      if (this.hasGccHighStepperTarget) this.gccHighStepperTarget.hidden = false
      if (this.hasStandardHeaderTarget) this.standardHeaderTarget.hidden = true
      this.microsoftSectionTarget.hidden = true
      this.googleSectionTarget.hidden = true
      if (this.hasGccHighSectionTarget) this.gccHighSectionTarget.hidden = false

      if (this.#orgId) {
        this.gccHighOrgIdTarget.value = this.#orgId
        this.gccHighOrgNameTarget.disabled = true
        this.gccHighOrgIdTarget.disabled = false
      } else if (this.#orgName) {
        this.gccHighOrgNameTarget.value = this.#orgName
        this.gccHighOrgIdTarget.disabled = true
        this.gccHighOrgNameTarget.disabled = false
      }

      this.continueButtonTarget.textContent = "Finish"
      this.#updateGccHighContinueButton()
      return
    }

    if (step === 3) {
      if (this.hasStandardStepperTarget) this.standardStepperTarget.hidden = false
      if (this.hasGccHighStepperTarget) this.gccHighStepperTarget.hidden = true
      if (this.hasStandardHeaderTarget) this.standardHeaderTarget.hidden = false
      if (this.hasGccHighSectionTarget) this.gccHighSectionTarget.hidden = true

      this.microsoftSectionTarget.hidden = !this.#microsoftSelected
      this.googleSectionTarget.hidden = !this.#googleSelected

      // Show "Enable <product>" variant when mapping already exists
      const addingToExisting = this.#microsoftSelected && this.#microsoftMapped
      this.microsoftSignInButtonTarget.hidden = addingToExisting
      this.microsoftEnableProductButtonTarget.hidden = !addingToExisting
      if (addingToExisting) {
        const product = this.ispmCheckboxTarget.checked ? "ISPM" : "ITDR"
        this.microsoftEnableProductLabelTarget.textContent = `Enable ${product}`
        this.microsoftSubtitleTarget.textContent = "Authentication already complete."
      } else {
        this.microsoftSubtitleTarget.innerHTML = "A <strong>Global Admin</strong> must approve the application."
      }
    }

    this.continueButtonTarget.textContent = step === 3 ? "Finish" : "Continue"
    // Steps 2 & 3: disabled until valid selection / sign-in.
    // Step 1: disabled until org selection (handled by selectionChanged).
    if (step === 2 || step === 3) this.continueButtonTarget.disabled = true
  }

  #finish() {
    if (this.#gccHighSelected) {
      track("Identity Provider Modal - Step Completed", { step: 3 })
      track("Identity Provider Modal - Finished", { org_id: this.#orgId, org_name: this.#orgName, products: ["itdr"], providers: ["microsoft"], environment: "gcc_high" })
      this.gccHighFormTarget.submit()
      return
    }

    const products = []
    if (this.itdrCheckboxTarget.checked) products.push("itdr")
    if (this.ispmCheckboxTarget.checked) products.push("ispm")
    const providers = []
    if (this.#microsoftSelected) providers.push("microsoft")
    if (this.#googleSelected) providers.push("google")
    track("Identity Provider Modal - Step Completed", { step: 3 })
    track("Identity Provider Modal - Finished", { org_id: this.#orgId, org_name: this.#orgName, products, providers })
    window.location.reload()
  }

  #updateGccHighContinueButton() {
    const hasOutput = this.hasGccHighScriptOutputTarget && this.gccHighScriptOutputTarget.value.trim().length > 0
    this.continueButtonTarget.disabled = !hasOutput
    this.continueButtonTarget.textContent = "Finish"
  }

  #updateStep3ContinueButton() {
    const microsoftVisible = this.#microsoftSelected
    const googleVisible = this.#googleSelected

    const microsoftConnected = microsoftVisible && this.#microsoftConnected
    const googleConnected = googleVisible && this.#googleConnected
    const microsoftErrored = microsoftVisible && this.#microsoftError !== null
    const googleErrored = googleVisible && this.#googleError !== null

    const microsoftResolved = !microsoftVisible || microsoftConnected || microsoftErrored
    const googleResolved = !googleVisible || googleConnected || googleErrored
    const anyConnected = microsoftConnected || googleConnected

    const allResolved = microsoftResolved && googleResolved
    this.continueButtonTarget.disabled = !(allResolved && anyConnected)

    const visibleCount = (microsoftVisible ? 1 : 0) + (googleVisible ? 1 : 0)
    const connectedCount = (microsoftConnected ? 1 : 0) + (googleConnected ? 1 : 0)
    const allConnected = allResolved && connectedCount === visibleCount
    this.continueButtonTarget.textContent = allResolved && connectedCount > 0 && connectedCount < visibleCount
      ? `Proceed with ${connectedCount} integration`
      : "Finish"
    this.cancelButtonTarget.hidden = allConnected
  }

  #resetStep3() {
    for (const provider of ["microsoft", "google"]) {
      this.#clearPollTimer(provider)
      if (this.#popups[provider] && !this.#popups[provider].closed) this.#popups[provider].close()
    }
    this.#popups = {}
    this.#microsoftConnected = false
    this.#googleConnected = false
    this.#microsoftError = null
    this.#googleError = null
    this.microsoftOauthProductItdrTarget.disabled = true
    this.microsoftOauthProductIspmTarget.disabled = true
    this.microsoftSectionTarget.hidden = true
    this.microsoftSignInButtonTarget.hidden = false
    this.microsoftSignInButtonTarget.disabled = false
    this.microsoftEnableProductButtonTarget.hidden = true
    this.microsoftEnableProductButtonTarget.disabled = false
    this.microsoftSubtitleTarget.innerHTML = "A <strong>Global Admin</strong> must approve the application."
    this.microsoftConnectedBadgeTarget.hidden = true
    this.microsoftOauthOrgIdTarget.disabled = false
    this.microsoftOauthOrgNameTarget.disabled = false
    this.googleSectionTarget.hidden = true
    this.googleStartCardTarget.hidden = false
    this.googleHeaderSpinnerTarget.hidden = true
    this.googleExpandableBodyTarget.style.gridTemplateRows = "0fr"
    this.googleExpandableDividerTarget.hidden = false
    this.googleStep1CardTarget.hidden = true
    this.googleStep2CardTarget.hidden = true
    this.googleConnectedBadgeTarget.hidden = true
    this.googleSignInButtonTarget.hidden = true
    this.googleSignInButtonTarget.disabled = false
    this.googleOauthOrgIdTarget.disabled = false
    this.googleOauthOrgNameTarget.disabled = false
    this.#hideProviderError("microsoft")
    this.#hideProviderError("google")
    if (this.hasGccHighSectionTarget) {
      this.gccHighSectionTarget.hidden = true
      this.gccHighScriptOutputTarget.value = ""
      this.gccHighOrgIdTarget.value = ""
      this.gccHighOrgNameTarget.value = ""
      this.gccHighOrgIdTarget.disabled = false
      this.gccHighOrgNameTarget.disabled = false
    }
    if (this.hasGccHighStepperTarget) this.gccHighStepperTarget.hidden = true
    if (this.hasStandardStepperTarget) this.standardStepperTarget.hidden = false
    if (this.hasStandardHeaderTarget) this.standardHeaderTarget.hidden = false
  }

  #resetStep2() {
    this.#microsoftSelected = false
    this.#googleSelected = false
    for (const card of ["ispm", "itdr", "microsoft", "google"]) {
      this[`${card}CheckboxTarget`].checked = false
      this[`${card}CheckboxTarget`].disabled = false
      this[`${card}TooltipTarget`].hidden = true
    }
    this.ispmSubtitleTarget.textContent = ""
    this.itdrSubtitleTarget.textContent = ""
    this.microsoftProviderSubtitleTarget.textContent = this.ispmVisibleValue ? "ITDR and ISPM" : ""
  }

  #computeStep2UIState() {
    return deriveStep2UIState({
      orgState: {
        hasMsItdr: this.#microsoftMapped && this.#itdrEntitled,
        hasMsIspm: this.#microsoftMapped && this.#ispmEntitled,
        hasGoogleItdr: this.#googleMapped && this.#itdrEntitled
      },
      accountAccess: {
        canAccessItdr: this.canAccessItdrValue,
        canAccessIspm: this.canAccessIspmValue
      },
      selections: {
        itdr: this.itdrCheckboxTarget.checked,
        ispm: this.ispmCheckboxTarget.checked,
        microsoft: this.microsoftCheckboxTarget.checked,
        google: this.googleCheckboxTarget.checked
      },
      ispmVisible: this.ispmVisibleValue
    })
  }

  #applyStep2UIState(state) {
    this.#applyCardState("ispm", state.ispm)
    this.#applyCardState("itdr", state.itdr)
    this.#applyCardState("microsoft", state.microsoft)
    this.#applyCardState("google", state.google)

    this.microsoftProviderSubtitleTarget.textContent = state.microsoft.subtitle

    this.#microsoftSelected = this.microsoftCheckboxTarget.checked
    this.#googleSelected = this.googleCheckboxTarget.checked
    this.continueButtonTarget.disabled = !state.continueEnabled
  }

  #applyCardState(card, cardState) {
    const checkbox = this[`${card}CheckboxTarget`]
    const tooltip = this[`${card}TooltipTarget`]

    checkbox.checked = cardState.checked
    checkbox.disabled = cardState.disabled

    if (card === "ispm" || card === "itdr") {
      this[`${card}SubtitleTarget`].textContent = cardState.subtitle
    }

    if (cardState.overlayTooltip) {
      tooltip.hidden = false
      const textEl = tooltip.querySelector('[role="tooltip"]')
      if (textEl && textEl.firstChild) textEl.firstChild.textContent = cardState.overlayTooltip
    } else {
      tooltip.hidden = true
    }
  }

  #showProviderError(provider, message) {
    const section = provider === "microsoft" ? this.microsoftErrorSectionTarget : this.googleErrorSectionTarget
    const msg = provider === "microsoft" ? this.microsoftErrorMessageTarget : this.googleErrorMessageTarget
    msg.textContent = message || "Authorization failed. Please try again."
    section.classList.remove("hidden")
  }

  #hideProviderError(provider) {
    const section = provider === "microsoft" ? this.microsoftErrorSectionTarget : this.googleErrorSectionTarget
    section.classList.add("hidden")
  }

  // Poll for popup closure so we can re-enable the sign-in button if the user
  // closes the popup without completing auth. Max 10 minutes (OneTimeToken TTL).
  #startPollTimer(provider) {
    this.#clearPollTimer(provider)
    const startedAt = Date.now()
    const maxDuration = 10 * 60 * 1000

    this.#pollTimers[provider] = setInterval(() => {
      const popup = this.#popups[provider]
      const expired = Date.now() - startedAt > maxDuration
      if (!popup || popup.closed || expired) {
        this.#clearPollTimer(provider)
        if (provider === "microsoft" && !this.#microsoftConnected) {
          this.microsoftSignInButtonTarget.disabled = false
        } else if (provider === "google" && !this.#googleConnected) {
          this.googleSignInButtonTarget.disabled = false
        }
      }
    }, 500)
  }

  #clearPollTimer(provider) {
    if (this.#pollTimers[provider]) {
      clearInterval(this.#pollTimers[provider])
      this.#pollTimers[provider] = null
    }
  }
}
